import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchProjectDocuments,
  createProjectDocument,
  updateProjectDocument,
  deleteProjectDocument,
  validateProjectDocuments,
  getEnableEmployees,
  fetchProjectsById,
  getEnableVehicles
} from "../api/administrationService";

export const useProjectDocuments = (projectId) => {
  const queryClient = useQueryClient();

  // Obtener los documentos requeridos de un proyecto
  const {
    data: documents = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ["projectDocuments", projectId],
    queryFn: () => fetchProjectDocuments(projectId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: !!projectId, // Solo ejecuta la consulta si projectId está definido
  });

  // ✅ Nuevo: obtener empleados habilitados y no habilitados
  const {
    data: empleadosHabilitacion = { habilitados: [], noHabilitados: [] },
    isLoading: isLoadingHabilitacion,
    error: errorHabilitacion,
    refetch: refetchEmpleadosHabilitacion,
  } = useQuery({
    queryKey: ["empleadosHabilitacion", projectId],
    queryFn: () => getEnableEmployees(projectId),
    enabled: !!projectId,
    staleTime: 0,
    //staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // ✅ Nuevo: obtener empleados habilitados y no habilitados
  const {
    data: vehiculosHabilitacion = { habilitados: [], noHabilitados: [] },
    isLoading: isLoadingVehiculoHabilitacion,
    error: errorVehiculoHabilitacion,
    refetch: refetchVehiculosHabilitacion,
  } = useQuery({
    queryKey: ["vehiculosHabilitacion", projectId],
    queryFn: () => getEnableVehicles(projectId),
    enabled: !!projectId,
    staleTime: 0,
    //staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Invalidar caché tras una mutación
  const invalidateDocumentsCache = () => {
    queryClient.invalidateQueries({ queryKey: ["projectDocuments", projectId] });
  };

  const useFetchProjectByID = (id) => {
    return useQuery({
      queryKey: ["project", id],
      queryFn: () => fetchProjectsById(id),
      //enabled: false,
      enabled: !!id, // Solo ejecuta si hay un ID válido
      staleTime: 1000 * 60 * 5,
    });
  };

  // Nueva función para validar documentos de un empleado/vehículo
  const validateDocuments = async (manifestId, entityId, entityType) => {
    try {
      let typeUser = "";
      if (entityType === 'employee') {
        typeUser = "CONDUCTOR";
      }
      if (entityType === 'vehicle') {
        typeUser = "VEHICULO";
      }

      const response = await validateProjectDocuments(manifestId, entityId, typeUser);
      return response;
    } catch (error) {
      console.error("Error validando documentos:", error);
      return null;
    }
  };

  // Mutación para crear un documento requerido
  const createMutation = useMutation({
    mutationFn: createProjectDocument,
    onSuccess: invalidateDocumentsCache,
  });

  // Mutación para actualizar un documento requerido
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateProjectDocument(id, data),
    onSuccess: invalidateDocumentsCache,
  });

  // Mutación para eliminar un documento requerido
  const deleteMutation = useMutation({
    mutationFn: deleteProjectDocument,
    onSuccess: invalidateDocumentsCache,
  });

  return {
    documents,
    error,
    isLoading,
    createMutation,
    updateMutation,
    deleteMutation,
    validateDocuments,
    empleadosHabilitacion,
    isLoadingHabilitacion,
    errorHabilitacion,
    useFetchProjectByID,
    vehiculosHabilitacion,
    isLoadingVehiculoHabilitacion,
    errorVehiculoHabilitacion
  };
};
